import React, { useEffect } from 'react';
import { Form, Select } from 'antd';

interface WorkLocationFieldProps {
  unitsList: any[];
  form: any;
  className?: string;
  singleSelect?: boolean;
}

/**
 * Shared WorkLocationField component for AddUser and EditUser forms
 * Displays a location field that is disabled when consultant checkbox is checked.
 * Technicians only ever get access to a single center, so `singleSelect` swaps the
 * control to a genuine single-select while the underlying `user_unit_name` form
 * value stays an array (matching the multi-select shape the backend expects) via
 * getValueProps/getValueFromEvent.
 */
const WorkLocationField: React.FC<WorkLocationFieldProps> = ({
  unitsList,
  form,
  className = '',
  singleSelect = false,
}) => {
  const consultantValue = Form.useWatch('consultant', form);

  // When switching into single-select (e.g. role changed to technician), trim
  // any previously-selected multiple centers down to one so the stored form
  // value can't silently outlive what the control displays.
  useEffect(() => {
    if (!singleSelect) return;
    const current = form.getFieldValue('user_unit_name');
    if (Array.isArray(current) && current.length > 1) {
      form.setFieldsValue({ user_unit_name: [current[0]] });
    }
  }, [singleSelect, form]);

  return (
    <Form.Item
      className={className}
      label="Work Location"
      name="user_unit_name"
      dependencies={['consultant']}
      getValueProps={(value) => ({
        value: singleSelect ? (Array.isArray(value) ? value[0] : value) : value,
      })}
      getValueFromEvent={(value) => (singleSelect ? (value ? [value] : []) : value)}
      rules={[
        ({ getFieldValue }) => ({
          validator(_, value) {
            const isConsultant = getFieldValue('consultant');
            // If consultant is checked, location is optional
            if (isConsultant) {
              return Promise.resolve();
            }
            // If consultant is not checked, location is required
            if (!value || value.length === 0) {
              return Promise.reject(new Error('Please select unit!'));
            }
            return Promise.resolve();
          },
        }),
      ]}
    >
      <Select
        placeholder={singleSelect ? 'Select Center' : 'Select Unit'}
        options={unitsList}
        allowClear
        mode={singleSelect ? undefined : 'multiple'}
        disabled={consultantValue}
      />
    </Form.Item>
  );
};

export default WorkLocationField;
